import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { EditableNotes, TextWithTooltip, CircleIcon, FlexSpace } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { TextPreviewModal } from '@ubeya/shared-web/components/Modal/TextPreviewModal';
import { ReactComponent as EditIcon } from '@ubeya/shared-web/assets/edit.svg';
import { isReadOnly } from 'shared/utils/timesheet';

const Wrapper = styled(FlexSpace)`
  width: 100%;
  height: 60px;
  min-width: 100px;
  gap: 10px;
`;

const EditIconWrapper = styled(CircleIcon)`
  min-width: 36px;
`;

const ReviewOnEmployeeRenderer = ({
  rowData,
  columnData: { key, readOnly, onUpdate, enablePreviewModal, isSingleRow, showEditIcon, onExternalUpdate }
}) => {
  const [inputValue, setInputValue] = useState(rowData[key] || '');
  const [showPreview, setShowPreview] = useState(false);
  const { subRows, summarized } = rowData;
  const { t } = useContext(ConfigContext);
  const notesRef = useRef();

  const updateValue = useCallback(
    (value) => {
      return rowData.isExternal
        ? onExternalUpdate?.({
            value,
            slotId: rowData.id,
            ...rowData
          })
        : onUpdate({
            value,
            ...rowData
          });
    },
    [onExternalUpdate, onUpdate, rowData]
  );

  const notesValue = useMemo(() => rowData[key] || '', [key, rowData]);

  const handleWrapperClick = useCallback(() => {
    if (enablePreviewModal && !showPreview) {
      setShowPreview(true);
    }
  }, [enablePreviewModal, showPreview]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.keyCode === 13 && isSingleRow) {
        setShowPreview(false);
        updateValue(inputValue);
        //if user hits escape reset the inputValue
      } else if (e.keyCode === 27) {
        setInputValue(rowData[key] || '');
      }
    },
    [inputValue, isSingleRow, key, rowData, updateValue]
  );

  if (subRows || summarized) {
    return <></>;
  }

  if (isReadOnly(rowData, readOnly) || rowData?.isJob) {
    return <TextWithTooltip>{notesValue}</TextWithTooltip>;
  }

  const inputParams = {
    id: key,
    value: inputValue,
    placeholder: t('addNote'),
    onChange: (e) => {
      const textValue = e.target.value;
      setInputValue(isSingleRow ? textValue.replaceAll('\n', '') : textValue);
    },
    onBlur: () => {
      updateValue(inputValue);
    },
    onKeyDown: handleKeyPress,
    showResizeIcon: false,
    style: { borderRadius: 8 }
  };

  return (
    <Wrapper onClick={handleWrapperClick}>
      <EditableNotes defaultValue={notesValue} onChange={updateValue} />

      {showEditIcon && <EditIconWrapper tooltip={t('edit')} icon={EditIcon} onClick={() => setShowPreview(true)} />}

      {showPreview && (
        <TextPreviewModal
          onClose={() => {
            setShowPreview(false);
          }}
          inputParams={inputParams}
          textAreaRef={notesRef}
        />
      )}
    </Wrapper>
  );
};

export default ReviewOnEmployeeRenderer;
